import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fadeIn, fadeOut } from 'src/app/animations';
import { BriefProposalDocument } from 'src/app/models/brief-approval-proposal-request.model';
import { ProposalDocument } from 'src/app/models/document.model';
import { Job } from 'src/app/models/job.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { ProposalService } from 'src/app/services/sections/job/proposal.service';

@Component({
	selector: 'board-proposal-form',
	templateUrl: './board-proposal-form.component.html',
	styleUrls: ['./board-proposal-form.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss'],
	animations: [fadeIn(300), fadeOut(300)]
})
export class BoardProposalFormComponent implements OnInit, OnDestroy, AfterViewInit {

	public chosenFiles: Map<number, ProposalDocument> = new Map<number, ProposalDocument>();
	public recommendedId: number;

	public formModel: FormGroup;

	public self = this;

	public readonly ButtonIcons = ButtonIcons;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;
	public moment = moment;

	public isLoading = false;

	public job: Job;
	public customerPreferences: SettingsPreferences;

	public get chosenFilesKeys(): number[] {
		return Array.from(this.chosenFiles.keys());
	}

	private unsubscriber$: Subject<void> = new Subject<void>();

	constructor(
		private activatedRoute: ActivatedRoute,
		private proposalService: ProposalService,
		private router: Router
	) {
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences as SettingsPreferences;

		this.formModel = new FormGroup({
			comment: new FormControl(null),
			type: new FormControl(null, [Validators.required, Validators.pattern(/\S+/)]),
			budget: new FormControl(null, [Validators.pattern(/^[0-9]{1,10}(\.[0-9]{1,2})?$/)])
		});

		const routingData = this.router.getCurrentNavigation().extras.state;
		if (routingData?.selectionData) {
			this.chosenFiles = routingData.selectionData;
		}

		// Carried payload is data that gets sent back and forth in order to keep state
		if (routingData?.carriedPayload) {
			this.formModel.get('budget').setValue(routingData.carriedPayload.budget);
			this.formModel.get('type').setValue(routingData.carriedPayload.type);
			this.formModel.get('comment').setValue(routingData.carriedPayload.comment);
		}
	}

	ngOnInit(): void {
		this.job = (this.activatedRoute.snapshot.data.jobDetails || {}) as Job;

		this.isLoading = false;
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.isSecondaryLoaderLoading = false;
	}

	ngOnDestroy() {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public validateNumberKey(event): boolean {
		return /[0-9\.]/.test(event.key) && (
			!this.formModel.get('budget').value ||
			/^[0-9]{1,10}(\.[0-9]{0,2})?$/.test(this.formModel.get('budget').value + event.key)
		);
	}

	public reformatBudgetOnBlur(): void {
		const control = this.formModel.get('budget');

		if (!control.value) {
			control.setValue(null);
		}
	}

	public clearFilesList(): void {
		this.chosenFiles.clear();
		this.recommendedId = null;
	}

	public removeDocument(fileId: number): void {
		this.chosenFiles.delete(fileId);

		if (this.recommendedId === fileId) {
			this.recommendedId = null;
		}
	}

	public openSelectionPage(): void {
		this.router.navigate(['documents'], { relativeTo: this.activatedRoute, state: { selectionData: this.chosenFiles, carriedPayload: this.formModel.getRawValue() } });
	}

	public submitProposal(): void {
		const model = this.formModel.getRawValue();
		model.budget = model.budget ? parseFloat(model.budget) : null;
		const files = Array.from(this.chosenFiles.keys());

		GlobalSettingsService.isSecondaryLoaderLoading = true;

		this.proposalService.propose(TokenHelper.getToken(this.activatedRoute), {
			type: model.type,
			budget: model.budget,
			comment: model.comment || '',
			documents: files.map(fileId => {
				const proposalDocument = new BriefProposalDocument();
				proposalDocument.documentId = fileId;
				proposalDocument.isRecommended = fileId === this.recommendedId;
				proposalDocument.proposalAmount = this.chosenFiles.get(fileId).proposalAmount;
				return proposalDocument;
			})
		})
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					this.router.navigate(['../'], { relativeTo: this.activatedRoute });
				},
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				});
	}

	public isAnyDocumentWithoutAmount(): boolean {
		return this.chosenFiles && Array.from(this.chosenFiles.values()).some(value => !value.proposalAmount);
	}

}
